import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class ImageUrlService {
  // Map to store path -> full URL mappings
  private imageRegistry = new Map<string, string>();

  /**
   * Extracts the path from a full URL, removing host and query parameters
   */
  extractImagePath(fullUrl: string): string {
    try {
      const url = new URL(fullUrl);
      // Extract the pathname without query parameters
      return url.pathname;
    } catch (error) {
      // If URL parsing fails, return the original URL
      console.warn("Failed to parse image URL:", fullUrl);
      return fullUrl;
    }
  }

  /**
   * Registers an image URL with its path for later resolution
   */
  registerImage(fullUrl: string): string {
    const path = this.extractImagePath(fullUrl);
    this.imageRegistry.set(path, fullUrl);
    return path;
  }

  /**
   * Resolves an image path back to its full URL
   */
  resolveImageUrl(path: string): string {
    // If it's already a full URL, return as-is (backward compatibility)
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    // Try to find the full URL from the registry
    const fullUrl = this.imageRegistry.get(path);
    if (fullUrl) {
      return fullUrl;
    }

    // If not found in registry, return the path (fallback behavior)
    console.warn("Image path not found in registry:", path);
    return path;
  }

  /**
   * Registers multiple images and returns their paths
   */
  registerImages(fullUrls: string[]): string[] {
    return fullUrls.map((url) => this.registerImage(url));
  }

  /**
   * Gets all registered image paths
   */
  getRegisteredPaths(): string[] {
    return Array.from(this.imageRegistry.keys());
  }

  /**
   * Gets all registered full URLs
   */
  getRegisteredUrls(): string[] {
    return Array.from(this.imageRegistry.values());
  }

  /**
   * Clears the image registry
   */
  clearRegistry(): void {
    this.imageRegistry.clear();
  }
}
